import { Box, CircularProgress, Collapse, IconButton } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useObserver } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  FB,
  FBDialog,
  FBMPIProcedureItemProps,
  FBMediaAlbumField,
  FBProcedureItemLocale,
  FBProcedureItemOmitAction,
  FBProcedureItemType,
  FBProvider,
  FBText,
  FBTooltip,
} from '..';
import { translate } from '../../../common/intl';
import { Button as AddButton } from '../../components/forms/fields-next';
import FBEquipmentTable from '../FBEquipmentTable/components/treelist';
import { FBEquipmentProps } from '../FBEquipmentTable/components/treelist/types';
import MaterialsTable from '../FBMaterialsTable/MaterialTable';
import { MaterialTableProps } from '../FBMaterialsTable/MaterialTable/types';
import FBMPIProcedureItemSerialsTable from '../FBMPIProcedureItemSerialsTable';
import { FBMPIProcedureItemSerialsTableProps } from '../FBMPIProcedureItemSerialsTable/types';
import useStyles from './FBMPIProcedureItem.styles';
import { withFBMPIProcedureItem } from './FBMPIProcedureItem.wrap';

// Draft mode renders a materials + equipment + serials section for every step.
// Mounting a Kendo grid for each one freezes large MPIs (a 78-step doc with
// materials on 30+ steps mounts 30+ grids at once). So:
//  - empty section  -> a lightweight "add" button; the grid mounts on click
//  - section w/ data -> a placeholder until it scrolls near the viewport, then the grid
const useLazyMount = (hasData: boolean, eager: boolean) => {
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(eager && hasData);

  useEffect(() => {
    if (mounted || !hasData) { return; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setMounted(true);
      }
    }, { rootMargin: '800px' });
    io.observe(el);
    return () => io.disconnect();
  }, [mounted, hasData]);

  return { ref, mounted, mount: () => setMounted(true) };
};

// Shown in place of a not-yet-mounted grid so the step doesn't look empty.
const SectionPlaceholder: React.FC<{ label: string }> = ({ label }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    style={{ gap: 8, minHeight: 120, color: '#8a8a8a', fontSize: 13 }}
    data-cy="section-loading"
  >
    <CircularProgress size={16} thickness={5} />
    {label}
  </Box>
);

const EquipmentSection: React.FC<FBEquipmentProps & { hasData: boolean }> = ({ hasData, ...props }) => {
  const { ref, mounted, mount } = useLazyMount(hasData, Boolean(props.isPreview));
  if (!hasData && !mounted) {
    return props.isDisabled ? null : (
      <AddButton fullWidth attached kind="add" data-cy="add-equipment" onClick={mount}>
        {translate('form.builder.add.item')}
      </AddButton>
    );
  }
  return (
    <div ref={ref}>
      {mounted ? <FBEquipmentTable {...props} /> : <SectionPlaceholder label={translate('common.loading')} />}
    </div>
  );
};

const MaterialsSection: React.FC<MaterialTableProps & { hasData: boolean }> = ({ hasData, ...props }) => {
  const { ref, mounted, mount } = useLazyMount(hasData, Boolean(props.isPreview));
  if (!hasData && !mounted) {
    return props.isDisabled ? null : (
      <AddButton fullWidth attached kind="add" data-cy="add-material" onClick={mount}>
        {translate('form.builder.procedure.material.table.add.part')}
      </AddButton>
    );
  }
  return (
    <div ref={ref}>
      {mounted ? <MaterialsTable {...props} /> : <SectionPlaceholder label={translate('common.loading')} />}
    </div>
  );
};

// Serials tables render for every step and each one runs a doc-wide serial-number
// scan + mounts a Kendo grid. Same viewport-lazy treatment.
const SerialsSection: React.FC<FBMPIProcedureItemSerialsTableProps & { eager: boolean }> = ({ eager, ...props }) => {
  const { ref, mounted } = useLazyMount(true, eager);
  return (
    <div ref={ref}>
      {mounted ? <FBMPIProcedureItemSerialsTable {...props} /> : <SectionPlaceholder label={translate('common.loading')} />}
    </div>
  );
};

const FBMPIProcedureItem: React.FC<FBMPIProcedureItemProps> = ({
  onClick,
  handleRemove,
  handleSelect,
  handleSelectionDelete,
  getItem,
  getLot,
  getProcedureLocale,
  getProcedureValues,
  isDateOverdue,
  mpiProcedureState,
  dialogState,
  isPreview,
  disabled,
  loading,
  type,
  stepIndex,
  value = [],
  name,
  isSelectVisible,
  defaultValue,
  redlineActive,
  isReleased,
  isFieldDisabled,
  isDesign,
  isDocumentMPI,
}) => {
  const classes = useStyles();
  const [isHover, setIsHover] = useState(false);
  const { serialsState } = FB.useStores();
  const isSerialsType = type === FBProcedureItemType.serials;
  useEffect(() => {
    if (isSerialsType) { serialsState?.load(); }
  }, [isSerialsType, serialsState]);
  // doc-level: skip mounting a serials section per step when serialization isn't in play
  const showSerials = useObserver(() => isSerialsType && (serialsState?.shouldShowSerials ?? false));
  const isUnrelatedToMaterialAndEquipment = ![FBProcedureItemType.materials, FBProcedureItemType.equipment].includes(type);
  const isAttachmentType = type === FBProcedureItemType.attachments;
  const hasItems = !isPreview || value.length > 0;
  const attachmentsLength = mpiProcedureState?.value?.attachments?.length ?? 0;
  const hasAttachments = attachmentsLength > 0;
  const showAttachments = isAttachmentType && (!isPreview || hasAttachments);
  const [isAttachmentExpanded, setIsAttachmentExpanded] = useState(hasAttachments);

  return (
    <FBProvider {...{ dialogState, mpiProcedureState }}>
      <FBDialog />
      {showAttachments && <Box className={classes.attachmentHeader}>
        <FBText
          {...{ loading }}
          boxProps={{
            flexGrow: 1,
            display: 'flex',
            p: 1.5,
          }}
          locale={FBProcedureItemLocale[type]}
        />
        <IconButton size="small" className={classes.iconButton} onClick={() => setIsAttachmentExpanded(prev => !prev)}>
          {isAttachmentExpanded ? <ExpandLessIcon className={classes.expandIcon} /> : <ExpandMoreIcon className={classes.expandIcon} />}
        </IconButton>
        {!isPreview && isUnrelatedToMaterialAndEquipment && !FBProcedureItemOmitAction.includes(type) && (
          <Box pt={1.6} pr={2}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}>
            <FBTooltip title="form.builder.add">
              { isHover
                ? <AddCircleIcon className={classes.addIcon}
                  data-cy= {`${FBProcedureItemLocale[type]}.btn`}
                  onClick={() => onClick?.()}
                  {...{ disabled }}
                />
                : <AddCircleOutlineOutlinedIcon className={classes.addIconOutline}
                  data-cy= {`${FBProcedureItemLocale[type]}.btn`}
                  onClick={() => onClick?.()}
                  {...{ disabled }}
                />
              }
            </FBTooltip>
          </Box>
        )}
      </Box>}
      {!FBProcedureItemOmitAction.includes(type) && (
        <Box className={classes.typeLabel}>
          <FBText {...{ loading }} locale={FBProcedureItemLocale[type]} />
        </Box>
      )}
      {showAttachments && (
        <Collapse in={isAttachmentExpanded}>
          <Box data-cy="mediaUploadField" mt={2}>
            <FBMediaAlbumField
              name={`${name}.attachments`}
              isTemplate={true}
              editorProperties={['copyableValue']}
              gutter={false}
              editorConfig={defaultValue}
              autosave={false}
              fieldType="mpiprocedure"
            />
          </Box>
        </Collapse>
      )}
      {type !== FBProcedureItemType.attachments && !loading && (
        <>
          {type === FBProcedureItemType.materials && hasItems && (
            <MaterialsSection
              key={(isSelectVisible && !redlineActive) ? 'enabled' : 'disabled'}
              hasData={value.length > 0}
              name={name}
              isEnableOnlyLotAddition={isSelectVisible && !redlineActive}
              isActive={!isPreview}
              type={type}
              isDisabled={disabled}
              isDesign={isDesign}
              items={value}
              getItem={getItem}
              handleRemove={handleRemove}
              handleSelectionDelete={handleSelectionDelete}
              isMPIProcedure={isDocumentMPI}
              isPreview={isPreview}
            />
          )}
          {type === FBProcedureItemType.equipment && hasItems && (
            <EquipmentSection
              key={(isSelectVisible && !redlineActive) ? 'enabled' : 'disabled'}
              hasData={value.length > 0}
              name={name}
              isDisabled={Boolean(disabled)}
              type={type}
              items={value}
              getItem={getItem}
              handleRemove={handleRemove}
              handleSelectionDelete={handleSelectionDelete}
              isDesign={isDesign}
              isPreview={isPreview}
              isMPIProcedure={isDocumentMPI}
              isEnableActualIdAddition={isSelectVisible && !redlineActive}
            />
          )}
          {showSerials && (
            <SerialsSection
              name={name}
              isDisabled={isFieldDisabled}
              eager={Boolean(isPreview)}
            />
          )}
        </>
      )}
    </FBProvider>
  );
};

export default withFBMPIProcedureItem(FBMPIProcedureItem);
